import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useFetchWithAuth } from '../session/useFetchWithAuth'
import { AlgoliaEvent } from './useSearchEvents'
import { Event, EventStatus } from './types'
import { convertToEvent } from './useSearchEvents'

const API_URL = import.meta.env.VITE_EPC_API_URL ?? ''

// Interface for API response
interface EventResponse {
  event: Event
  success: boolean
  errors?: {
    message: string
    path?: string[]
  }[]
}

// Function to find an event by ID
export const useFindEvent = (eventId: string) => {
  const fetchWithAuth = useFetchWithAuth()

  return useQuery({
    queryKey: ['event', eventId],
    queryFn: async () => {
      if (!eventId) {
        throw new Error('Event ID is required')
      }

      const response = await fetchWithAuth(`${API_URL}/events/${eventId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      })

      if (!response) {
        throw new Error('Event not found')
      }

      return {
        response: {
          event: response,
          success: true,
        },
      }
    },
    enabled: !!eventId,
    staleTime: 60 * 1000, // 1 minute
  })
}

// Hook for cancelling an event
export const useCancelEvent = () => {
  const fetchWithAuth = useFetchWithAuth()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async ({
      eventId,
      reason,
    }: {
      eventId: string
      reason?: string | null
    }) => {
      const response = await fetchWithAuth(
        `${API_URL}/events/${eventId}/cancel`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason }),
        }
      )

      return { response, eventId }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['event', data.eventId] })
      queryClient.invalidateQueries({ queryKey: ['adminEvents'] })
    },
  })

  return [
    async (variables: {
      variables: { eventId: string; reason?: string | null }
    }) => {
      return mutation.mutateAsync({
        eventId: variables.variables.eventId,
        reason: variables.variables.reason,
      })
    },
    { loading: mutation.isPending, error: mutation.error },
  ] as const
}

// Hook for denying an event
export const useDenyEvent = () => {
  const fetchWithAuth = useFetchWithAuth()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async ({
      eventId,
      reason,
    }: {
      eventId: string
      reason?: string | null
    }) => {
      const response = await fetchWithAuth(
        `${API_URL}/events/${eventId}/deny`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason }),
        }
      )

      return { response, eventId }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['event', data.eventId] })
      queryClient.invalidateQueries({ queryKey: ['adminEvents'] })
    },
  })

  return [
    async (variables: {
      variables: { eventId: string; reason?: string | null }
    }) => {
      return mutation.mutateAsync({
        eventId: variables.variables.eventId,
        reason: variables.variables.reason,
      })
    },
    { loading: mutation.isPending, error: mutation.error },
  ] as const
}

// Hook for destroying (deleting) an event
export const useDestroyEvent = () => {
  const fetchWithAuth = useFetchWithAuth()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async ({ eventId }: { eventId: string }) => {
      const response = await fetchWithAuth(`${API_URL}/events/${eventId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      })

      return { response, eventId }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['event', data.eventId] })
      queryClient.invalidateQueries({ queryKey: ['adminEvents'] })
    },
  })

  return [
    async (variables: { variables: { eventId: string } }) => {
      return mutation.mutateAsync({ eventId: variables.variables.eventId })
    },
    { loading: mutation.isPending, error: mutation.error },
  ] as const
}

// Hook for publishing an event
export const usePublishEvent = () => {
  const fetchWithAuth = useFetchWithAuth()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async ({ eventId }: { eventId: string }) => {
      const response = await fetchWithAuth(
        `${API_URL}/events/${eventId}/publish`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }
      )

      return { response, eventId }
    },
    onSuccess: (data) => {
      // Invalidate the single event query to refetch updated data
      queryClient.invalidateQueries({ queryKey: ['event', data.eventId] })
      // Also invalidate the events list
      queryClient.invalidateQueries({ queryKey: ['adminEvents'] })
    },
  })

  return [
    async (variables: { variables: { eventId: string } }) => {
      return mutation.mutateAsync({ eventId: variables.variables.eventId })
    },
    { loading: mutation.isPending, error: mutation.error },
  ] as const
}

// Updated usePaginatedEvents hook using React Query
export const usePaginatedEventsQuery = (options: {
  queryString?: string
  facetFilters?: string[]
  page?: number
  pageSize?: number
}) => {
  const fetchWithAuth = useFetchWithAuth()

  const {
    queryString = '',
    facetFilters = [],
    page = 0,
    pageSize = 12,
  } = options

  return useQuery({
    queryKey: ['adminEvents', queryString, facetFilters, page, pageSize],
    queryFn: async () => {
      // Build query params
      const queryParams = new URLSearchParams()

      if (queryString) queryParams.append('query', queryString)
      if (page !== undefined) queryParams.append('page', page.toString())
      if (pageSize !== undefined)
        queryParams.append('limit', pageSize.toString())
      queryParams.append('admin_search', 'true')

      // Add facet filters if present
      if (Array.isArray(facetFilters) && facetFilters.length > 0) {
        facetFilters.forEach((filter) => {
          queryParams.append('facet_filters', filter)
        })
      }

      const requestUrl = `${API_URL}/events/search?${queryParams.toString()}`

      const response = await fetchWithAuth(requestUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      })

      return {
        search: {
          events: {
            nodes: response?.items?.map(convertToEvent) || [],
            searchInfo: {
              page: response?.page || 0,
              pageCount: response?.pages || 0,
              facets: JSON.stringify(response?.facets || {}),
            },
          },
          success: true,
        },
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}
